import React, { useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import PromoActivityFunnelChart, { type PromoActivityFunnelStage } from '~/components/Promo/PromoActivityFunnelChart';

import { useGetPromoActivitySummary } from '~/hooks/apis/promo/useGetPromoActivitySummary';

import { type PromoActivitySummaryTotals } from '~/services/PromoService';

type EntityType = 'promo' | 'voucher';

type ActivityDetailLink = {
  label: string;
  to: string;
};

const EMPTY_TOTALS: PromoActivitySummaryTotals = {
  eventCount: 0,
  usageCount: 0,
  assignmentCount: 0,
  autoAssignmentCount: 0,
  byEventType: {},
  recentApplyStats: {
    requestCount: 0,
    successfulCount: 0,
    unsuccessfulRequestCount: 0,
    unsuccessfulLogCount: 0,
  },
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load recent activity.';
}

function formatEventType(eventType: string): string {
  return eventType
    .split('_')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}

function renderActivityMetric(label: string, value: number, detailLink?: ActivityDetailLink) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 88 }}>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      {detailLink ? (
        <Button
          component={Link}
          to={detailLink.to}
          variant="text"
          size="small"
          sx={{ justifyContent: 'flex-start', px: 0 }}
        >
          {value.toLocaleString()} {detailLink.label}
        </Button>
      ) : (
        <Typography variant="body1">{value.toLocaleString()}</Typography>
      )}
    </Stack>
  );
}

/** Compact recent-activity totals for one promo or voucher; the aggregate tester remains available for cross-entity analysis. */
export default function PromoActivityEntitySummaryCard({
  entityId,
  entityType,
  detailLinks = {},
}: {
  entityId: string;
  entityType: EntityType;
  /** Links to detail already available on the current page; omitted metrics remain informational. */
  detailLinks?: Partial<Record<'requests' | 'logs' | 'failures', ActivityDetailLink>>;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, error, isFetching } = useGetPromoActivitySummary({ enabled: Boolean(entityId) });
  const totals = useMemo(() => {
    if (!data) {
      return EMPTY_TOTALS;
    }
    return (entityType === 'promo' ? data.promoCodes : data.vouchers)[entityId] ?? EMPTY_TOTALS;
  }, [data, entityId, entityType]);
  const failures = totals.recentApplyStats.unsuccessfulRequestCount + totals.recentApplyStats.unsuccessfulLogCount;
  const activityWindow = useMemo(() => {
    if (!data?.windowStart || !data.windowEnd) {
      return null;
    }
    return `${dayjs(data.windowStart).format('D MMM, h:mm a')} – ${dayjs(data.windowEnd).format('D MMM, h:mm a')}`;
  }, [data?.windowEnd, data?.windowStart]);
  const lastActivityLabel = useMemo(() => {
    if (!totals.lastOccurredAt) {
      return null;
    }

    const lastActivity = dayjs(totals.lastOccurredAt);
    return lastActivity.isValid() ? lastActivity.fromNow() : null;
  }, [totals.lastOccurredAt]);
  const funnelStages = useMemo<Array<PromoActivityFunnelStage>>(
    () =>
      entityType === 'promo'
        ? [
            { label: 'Requests', value: totals.recentApplyStats.requestCount },
            { label: 'Applies', value: totals.recentApplyStats.successfulCount },
            { label: 'Uses', value: totals.usageCount },
          ]
        : [
            {
              label: 'Assignments',
              value: totals.assignmentCount,
              breakdown:
                totals.autoAssignmentCount > 0 && totals.autoAssignmentCount <= totals.assignmentCount
                  ? [
                      { label: 'Other', value: totals.assignmentCount - totals.autoAssignmentCount },
                      { label: 'Auto', value: totals.autoAssignmentCount },
                    ]
                  : undefined,
            },
            { label: 'Uses', value: totals.usageCount },
          ],
    [entityType, totals],
  );
  const eventTypes = useMemo(
    () =>
      Object.entries(totals.byEventType)
        .filter(([, count]) => count > 0)
        .sort(
          ([firstEventType, firstCount], [secondEventType, secondCount]) =>
            secondCount - firstCount || firstEventType.localeCompare(secondEventType),
        ),
    [totals.byEventType],
  );

  if (error) {
    return <Alert severity="warning">Recent activity is unavailable: {getErrorMessage(error)}</Alert>;
  }

  return (
    <Accordion
      component="section"
      expanded={isExpanded}
      onChange={(_, nextExpanded) => setIsExpanded(nextExpanded)}
      disableGutters
      variant="outlined"
      aria-label="Recent activity"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">Recent activity</Typography>
          <Tooltip title="Cached telemetry window of about 36 hours; not a durable reporting or reconciliation source.">
            <InfoOutlinedIcon aria-label="About recent activity telemetry" color="action" fontSize="small" />
          </Tooltip>
          {isFetching && <CircularProgress size={16} aria-label="Loading recent activity" />}
          {activityWindow ? (
            <Typography variant="caption" color="text.secondary">
              {activityWindow}
            </Typography>
          ) : null}
          <Chip size="small" label={`${totals.eventCount.toLocaleString()} events`} />
          {lastActivityLabel ? <Chip size="small" label={`last activity ${lastActivityLabel}`} /> : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {entityType === 'promo' ? (
            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {renderActivityMetric('Requests', totals.recentApplyStats.requestCount, detailLinks.requests)}
              {renderActivityMetric('Applies', totals.recentApplyStats.successfulCount, detailLinks.requests)}
              {renderActivityMetric('Uses', totals.usageCount, detailLinks.logs)}
              {renderActivityMetric('Assignments', totals.assignmentCount)}
              {renderActivityMetric('Failures', failures, detailLinks.failures)}
            </Stack>
          ) : (
            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {renderActivityMetric('Assignments', totals.assignmentCount)}
              {totals.autoAssignmentCount > 0 && renderActivityMetric('Auto assignments', totals.autoAssignmentCount)}
              {renderActivityMetric('Uses', totals.usageCount)}
              {renderActivityMetric('Events', totals.eventCount)}
            </Stack>
          )}
          {isExpanded && <PromoActivityFunnelChart stages={funnelStages} />}
          {isExpanded && eventTypes.length > 0 ? (
            <Stack spacing={0.5}>
              <Typography color="text.secondary" variant="caption">
                Event types
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {eventTypes.map(([eventType, count]) => (
                  <Chip
                    key={eventType}
                    label={`${formatEventType(eventType)} · ${count.toLocaleString()}`}
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
